import React, { useState } from "react";
import { resolveImageUrl } from "../utils/image";

interface RemoteImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
    src: string | null | undefined;
    fallback?: string;
    containerClassName?: string;
}

/**
 * A production-ready image component that:
 * 1. Automatically resolves URLs (handling localhost healing).
 * 2. Handles loading states.
 * 3. Provides a fallback if the image fails to load or is missing.
 */
export const RemoteImage: React.FC<RemoteImageProps> = ({
    src,
    fallback = "/assets/placeholder-image.png", // Ensure this exists or provide a default
    containerClassName = "",
    className = "",
    alt = "Image",
    ...props
}) => {
    const resolvedSrc = resolveImageUrl(src);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);

    const handleLoad = () => {
        setLoading(false);
    };

    const handleError = () => {
        setLoading(false);
        setError(true);
    };

    // If src is missing and no fallback is provided in props, we could show a default
    const finalSrc = error || !resolvedSrc ? fallback : resolvedSrc;

    return (
        <div className={`relative overflow-hidden ${containerClassName}`}>
            {loading && (
                <div className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                </div>
            )}
            <img
                src={finalSrc}
                alt={alt}
                onLoad={handleLoad}
                onError={handleError}
                className={`${className} ${loading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
                {...props}
            />
        </div>
    );
};
